import React, { type ReactNode } from 'react';

import { Colors } from '@treneo/shared/themes';

import type { SxProps, Theme } from '@mui/material';
import { IconButton as MuiIconButton } from '@mui/material';

type IconButtonVariant = 'primary' | 'secondary' | 'default';
type IconButtonSize = 'small' | 'medium' | 'large';

type Props = Readonly<{
  children: ReactNode;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  disabled?: boolean;
  'aria-label'?: string;
  title?: string;
  color?:
    | 'inherit'
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning';
  className?: string;
  sx?: SxProps<Theme>;
}>;

const getVariantStyles = (variant: IconButtonVariant) => {
  switch (variant) {
    case 'primary':
      return {
        backgroundColor: Colors.red,
        color: Colors.white,
        '&:hover': {
          backgroundColor: Colors.blue,
          color: Colors.white
        }
      };
    case 'secondary':
      return {
        backgroundColor: Colors.darkGrey,
        color: Colors.white,
        '&:hover': {
          backgroundColor: Colors.textColor
        }
      };
    default:
      return {};
  }
};

const getSizeStyles = (size: IconButtonSize) => {
  switch (size) {
    case 'small':
      return { width: 32, height: 32 };
    case 'large':
      return { width: 48, height: 48 };
    default:
      return { width: 40, height: 40 };
  }
};

export const IconButton = ({
  children,
  onClick,
  variant = 'default',
  size = 'medium',
  disabled = false,
  'aria-label': ariaLabel,
  title,
  color,
  className,
  sx
}: Props) => {
  const variantStyles = getVariantStyles(variant);
  const sizeStyles = getSizeStyles(size);

  return (
    <MuiIconButton
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={title}
      color={color}
      className={className}
      sx={{
        ...variantStyles,
        ...sizeStyles,
        ...sx
      }}
    >
      {children}
    </MuiIconButton>
  );
};
